<script setup>
import { ref, computed, onMounted, onUnmounted, onActivated, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { Line, Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const store = useStore();
const route = useRoute();

// State
const isLoading = ref(true);
const refreshInterval = ref(null);

// Date range
const dateRange = ref({
  start: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .slice(0, 10),
  end: new Date().toISOString().slice(0, 10),
});

// Getters
const tickets = useMapGetter('tickets/getTickets');
const trends = useMapGetter('tickets/getTrends');

// Days of week in Spanish
const daysOfWeek = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];


// Tickets are already filtered by date range from the API
const filteredTickets = computed(() => tickets.value || []);

// Calculate metrics
const metrics = computed(() => {
  const filtered = filteredTickets.value;
  const total = filtered.length;
  const enProgreso = filtered.filter(t =>
    ['in_progress', 'waiting_client', 'waiting_internal'].includes(t.status)
  ).length;
  const resueltos = filtered.filter(t => t.status === 'resolved').length;
  const urgentes = filtered.filter(t => t.priority === 'urgent').length;

  const currentTrends = trends.value || {};

  return {
    total,
    enProgreso,
    resueltos,
    urgentes,
    tendencia: currentTrends.total || { value: 0, isPositive: true },
    enProgresoTrend: currentTrends.inProgress || { value: 0, isPositive: true },
    resueltosTrend: currentTrends.resolved || { value: 0, isPositive: true },
    urgentesTrend: currentTrends.urgent || { value: 0, isPositive: true },
  };
});

// Get last week trend data (uses dateRange.end as reference, capped at today)
function getLastWeekTrend(ticketsList) {
  const today = new Date();
  const rangeEnd = new Date(dateRange.value.end + 'T23:59:59');
  const reference = rangeEnd < today ? rangeEnd : today;
  const days = [];

  for (let i = 6; i >= 0; i--) {
    const d = new Date(reference);
    d.setDate(reference.getDate() - i);
    days.push(d);
  }

  return days.map(date => {
    const dayName = daysOfWeek[date.getDay()];

    const ticketsCreated = ticketsList.filter(t => {
      const created = new Date(t.created_at);
      return (
        created.getFullYear() === date.getFullYear() &&
        created.getMonth() === date.getMonth() &&
        created.getDate() === date.getDate()
      );
    }).length;

    const ticketsResolved = ticketsList.filter(t => {
      if (!t.resolved_at) return false;
      const resolved = new Date(t.resolved_at);
      return (
        resolved.getFullYear() === date.getFullYear() &&
        resolved.getMonth() === date.getMonth() &&
        resolved.getDate() === date.getDate()
      );
    }).length;

    return {
      day: dayName,
      tickets: ticketsCreated,
      resueltos: ticketsResolved,
    };
  });
}

// Get category data grouped by ticket_category
function getCategoryData(ticketsList) {
  const counts = {};

  ticketsList.forEach(t => {
    const name = t.ticket_category?.name || 'Sin categoría';
    counts[name] = (counts[name] || 0) + 1;
  });

  return Object.entries(counts)
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);
}

// Get resolution data: cumulative daily rate across the date range
function getResolutionData(ticketsList) {
  const now = new Date();
  const start = new Date(dateRange.value.start + 'T00:00:00');
  const end = new Date(dateRange.value.end + 'T23:59:59');
  const rangeEnd = end < now ? end : now;

  const total = ticketsList.length;
  const days = [];
  const d = new Date(start);
  while (d <= rangeEnd) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }

  return days.map(day => {
    const dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);
    const cutoff = dayEnd < now ? dayEnd : now;

    const resueltos = ticketsList.filter(t => {
      if (!t.resolved_at) return false;
      return new Date(t.resolved_at) <= cutoff;
    }).length;

    const tasa = total > 0 ? Math.round((resueltos / total) * 100) : 0;
    const time = `${day.getDate()}/${day.getMonth() + 1}`;

    return { time, tasa, resueltos, total };
  });
}

// Chart data
const ticketTrendData = computed(() => getLastWeekTrend(filteredTickets.value));
const categoryData = computed(() => getCategoryData(filteredTickets.value));
const resolutionData = computed(() => getResolutionData(tickets.value || []));

// Line chart configuration (Tendencia de Tickets)
const lineChartData = computed(() => ({
  labels: ticketTrendData.value.map(d => d.day),
  datasets: [
    {
      label: 'Tickets',
      data: ticketTrendData.value.map(d => d.tickets),
      borderColor: '#3b82f6',
      backgroundColor: 'transparent',
      tension: 0.4,
      borderWidth: 2,
    },
    {
      label: 'Resueltos',
      data: ticketTrendData.value.map(d => d.resueltos),
      borderColor: '#10b981',
      backgroundColor: 'transparent',
      tension: 0.4,
      borderWidth: 2,
    },
  ],
}));

const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top',
    },
    tooltip: {
      backgroundColor: '#fff',
      titleColor: '#1e293b',
      bodyColor: '#64748b',
      borderColor: '#e2e8f0',
      borderWidth: 1,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: '#f1f5f9',
      },
      ticks: {
        color: '#64748b',
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: '#64748b',
      },
    },
  },
};

// Area chart configuration (Tasa de Resolución)
const areaChartData = computed(() => ({
  labels: resolutionData.value.map(d => d.time),
  datasets: [
    {
      label: 'Tasa %',
      data: resolutionData.value.map(d => d.tasa),
      borderColor: '#10b981',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      fill: true,
      tension: 0.4,
      borderWidth: 2,
    },
  ],
}));

const areaChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: '#fff',
      titleColor: '#1e293b',
      bodyColor: '#64748b',
      borderColor: '#e2e8f0',
      borderWidth: 1,
      callbacks: {
        label: function (context) {
          const data = resolutionData.value[context.dataIndex];
          return [
            `Tasa: ${data.tasa}%`,
            `${data.resueltos} de ${data.total} resueltos`,
          ];
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      grid: {
        color: '#f1f5f9',
      },
      ticks: {
        color: '#64748b',
        callback: value => value + '%',
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: '#64748b',
      },
    },
  },
};

// Bar chart configuration (Tickets por Categoría)
const barChartData = computed(() => ({
  labels: categoryData.value.map(d => d.category),
  datasets: [
    {
      label: 'Tickets',
      data: categoryData.value.map(d => d.count),
      backgroundColor: '#3b82f6',
      borderRadius: 8,
      barThickness: 40,
    },
  ],
}));

const barChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      backgroundColor: '#fff',
      titleColor: '#1e293b',
      bodyColor: '#64748b',
      borderColor: '#e2e8f0',
      borderWidth: 1,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: '#f1f5f9',
      },
      ticks: {
        color: '#64748b',
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: '#64748b',
        font: {
          size: 11,
        },
      },
    },
  },
};

// Methods
const fetchData = async () => {
  try {
    await Promise.all([
      store.dispatch('tickets/fetchDashboardTickets', {
        start_date: dateRange.value.start,
        end_date: dateRange.value.end,
      }),
      store.dispatch('tickets/fetchMeta'),
      store.dispatch('agents/get'),
    ]);
  } finally {
    isLoading.value = false;
  }
};

// Lifecycle
onMounted(() => {
  fetchData();
  // Auto-refresh every 30 seconds
  refreshInterval.value = setInterval(fetchData, 30000);
});

// Also fetch on activation (for keep-alive scenarios)
onActivated(() => {
  isLoading.value = true;
  fetchData();
});

onUnmounted(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
  }
});

// Watch for route changes
watch(
  () => route.name,
  newName => {
    if (newName === 'tickets_dashboard') {
      isLoading.value = true;
      fetchData();
    }
  }
);

// Watch date range changes
watch(
  dateRange,
  () => {
    // Refetch tickets when date range changes
    store.dispatch('tickets/fetchDashboardTickets', {
      start_date: dateRange.value.start,
      end_date: dateRange.value.end,
    });
  },
  { deep: true }
);
</script>

<template>
  <div
    class="flex flex-col w-full h-full min-h-0 overflow-auto p-6 gap-6 bg-n-bg"
  >
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-16">
      <div class="flex flex-col items-center gap-4">
        <div class="i-lucide-loader-2 text-4xl text-n-brand animate-spin" />
        <span class="text-n-slate-11">Cargando dashboard...</span>
      </div>
    </div>

    <template v-else>
      <h1 class="text-xl font-semibold text-n-slate-12 mb-2">Mis Tickets</h1>
      <!-- Date Range Filters -->
      <div class="flex gap-4 items-center">
        <label class="text-sm font-medium text-n-slate-12">Desde:</label>
        <input
          v-model="dateRange.start"
          type="date"
          class="px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
        />
        <label class="text-sm font-medium text-n-slate-12">Hasta:</label>
        <input
          v-model="dateRange.end"
          type="date"
          class="px-3 py-2 rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
        />
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <!-- Total de Tickets -->
        <div
          class="flex flex-col gap-2 p-4 rounded-lg bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm hover:shadow-md transition-shadow"
        >
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-11">Total de Tickets</span>
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/20"
            >
              <span
                class="i-lucide-ticket text-xl text-blue-600 dark:text-blue-400"
              />
            </div>
          </div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ metrics.total }}
          </div>
          <p class="text-xs text-n-slate-11">
            <span
              :class="
                metrics.tendencia.isPositive ? 'text-green-600' : 'text-red-600'
              "
            >
              {{ metrics.tendencia.isPositive ? '+' : '-'
              }}{{ metrics.tendencia.value }}%
            </span>
            vs periodo anterior
          </p>
        </div>

        <!-- En Progreso -->
        <div
          class="flex flex-col gap-2 p-4 rounded-lg bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm hover:shadow-md transition-shadow"
        >
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-11">En Progreso</span>
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/20"
            >
              <span
                class="i-lucide-clock text-xl text-blue-600 dark:text-blue-400"
              />
            </div>
          </div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ metrics.enProgreso }}
          </div>
          <p class="text-xs text-n-slate-11">
            <span
              :class="
                metrics.enProgresoTrend.isPositive
                  ? 'text-green-600'
                  : 'text-red-600'
              "
            >
              {{ metrics.enProgresoTrend.isPositive ? '+' : '-'
              }}{{ metrics.enProgresoTrend.value }}%
            </span>
            vs periodo anterior
          </p>
        </div>

        <!-- Resueltos -->
        <div
          class="flex flex-col gap-2 p-4 rounded-lg bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm hover:shadow-md transition-shadow"
        >
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-11">Resueltos</span>
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/20"
            >
              <span
                class="i-lucide-check-circle-2 text-xl text-green-600 dark:text-green-400"
              />
            </div>
          </div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ metrics.resueltos }}
          </div>
          <p class="text-xs text-n-slate-11">
            <span
              :class="
                metrics.resueltosTrend.isPositive
                  ? 'text-green-600'
                  : 'text-red-600'
              "
            >
              {{ metrics.resueltosTrend.isPositive ? '+' : '-'
              }}{{ metrics.resueltosTrend.value }}%
            </span>
            vs periodo anterior
          </p>
        </div>

        <!-- Urgentes -->
        <div
          class="flex flex-col gap-2 p-4 rounded-lg bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm hover:shadow-md transition-shadow"
        >
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-11">Urgentes</span>
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20"
            >
              <span
                class="i-lucide-alert-circle text-xl text-red-600 dark:text-red-400"
              />
            </div>
          </div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ metrics.urgentes }}
          </div>
          <p class="text-xs text-n-slate-11">
            <span
              :class="
                metrics.urgentesTrend.isPositive
                  ? 'text-green-600'
                  : 'text-red-600'
              "
            >
              {{ metrics.urgentesTrend.isPositive ? '+' : '-'
              }}{{ metrics.urgentesTrend.value }}%
            </span>
            vs periodo anterior
          </p>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="grid gap-6 lg:grid-cols-2">
        <!-- Tendencia de Tickets (Line Chart) -->
        <div
          class="p-4 rounded-lg bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm"
        >
          <div class="mb-4">
            <h3 class="text-lg font-semibold text-n-slate-12">
              Tendencia de Tickets
            </h3>
            <p class="text-sm text-n-slate-11">
              Tickets creados vs resueltos (última semana)
            </p>
          </div>
          <div style="height: 300px">
            <Line :data="lineChartData" :options="lineChartOptions" />
          </div>
        </div>

        <!-- Tasa de Resolución en Tiempo Real (Area Chart) -->
        <div
          class="p-4 rounded-lg bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm"
        >
          <div class="mb-4">
            <h3 class="text-lg font-semibold text-n-slate-12">
              Tasa de Resolución en Tiempo Real
            </h3>
            <p class="text-sm text-n-slate-11">
              Tasa acumulada de resolución por día del período
            </p>
          </div>
          <div style="height: 300px">
            <Line :data="areaChartData" :options="areaChartOptions" />
          </div>
        </div>
      </div>

      <!-- Tickets por Categoría (Bar Chart) -->
      <div
        class="p-4 rounded-lg bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm"
      >
        <div class="mb-4">
          <h3 class="text-lg font-semibold text-n-slate-12">
            Tickets por Categoría
          </h3>
          <p class="text-sm text-n-slate-11">
            Distribución de tickets por tipo
          </p>
        </div>
        <div style="height: 300px">
          <Bar :data="barChartData" :options="barChartOptions" />
        </div>
      </div>
    </template>
  </div>
</template>
